import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardSectionHeader,
  TablePagePagination,
  dashboardDataTable as dt,
} from "src/components/common";
import RestaurantAiExecutiveSummary from "../RestaurantAiExecutiveSummary";
import RestaurantHeaderActions, { type RestaurantHeaderActionsProps } from "../RestaurantHeaderActions";
import RestaurantKpiCard from "../RestaurantKpiCard";
import RestaurantSectionCard from "../RestaurantSectionCard";
import RestaurantHorizontalBars from "../charts/RestaurantHorizontalBars";
import RestaurantCategoryBars from "../charts/RestaurantCategoryBars";
import { MENU_ITEMS } from "../../data/restaurantDemoData";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";
import type { RestaurantFilters, RestaurantMetrics } from "../../utils/restaurantMetrics";

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  grills: "restaurantAnalytics.categories.grills",
  rice_dishes: "restaurantAnalytics.categories.riceDishes",
  sandwiches: "restaurantAnalytics.categories.sandwiches",
  appetizers: "restaurantAnalytics.categories.appetizers",
  beverages: "restaurantAnalytics.categories.beverages",
  desserts: "restaurantAnalytics.categories.desserts",
};

interface RestaurantMenuTabProps {
  metrics: RestaurantMetrics;
  filters: RestaurantFilters;
  headerProps: RestaurantHeaderActionsProps;
}

const RestaurantMenuTab: FC<RestaurantMenuTabProps> = ({ metrics, filters, headerProps }) => {
  const { t, i18n } = useTranslation();
  const fmt = useRestaurantFormatters();
  const isRTL = i18n.language.toLowerCase().startsWith("ar");

  const itemName = (id: string) => {
    const item = MENU_ITEMS.find(m => m.id === id);
    if (!item) {return id;}
    return isRTL ? item.nameAr : item.name;
  };

  const totalUnits = metrics.menu.reduce((sum, item) => sum + item.units, 0);
  const topItemsBars = metrics.menu.slice(0, 8).map(item => ({
    id: item.id,
    label: itemName(item.id),
    value: item.revenue,
  }));
  const categoryBars = metrics.categories.map(category => ({
    id: category.id,
    label: t(CATEGORY_LABEL_KEYS[category.id] ?? category.id, { defaultValue: category.id }),
    value: category.revenue,
  }));
  const slowMovers = [...metrics.menu].sort((a, b) => a.units - b.units).slice(0, 5);
  const topSellingItem = metrics.menu[0];

  const ROWS_PER_PAGE = 10;
  const [menuPage, setMenuPage] = useState(1);
  const menuTotalPages = Math.max(1, Math.ceil(metrics.menu.length / ROWS_PER_PAGE));
  const paginatedMenuItems = useMemo(
    () => metrics.menu.slice((menuPage - 1) * ROWS_PER_PAGE, menuPage * ROWS_PER_PAGE),
    [metrics.menu, menuPage],
  );
  useEffect(() => setMenuPage(1), [metrics.menu]);

  return (
    <div className="restaurant-analytics-dashboard__section">
      <DashboardSectionHeader
        title={t("restaurantAnalytics.tabs.menu", { defaultValue: "Menu Analytics" })}
        subtitle={t("restaurantAnalytics.header.menuSubtitle", {
          defaultValue: "Top sellers, categories, and slow-moving items.",
        })}
        sticky
        actions={<RestaurantHeaderActions {...headerProps} />}
      />

      <RestaurantAiExecutiveSummary tab="menu" metrics={metrics} filters={filters} />

      <div className="restaurant-analytics-dashboard__kpi-row">
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.unitsSold", { defaultValue: "Units sold" })}
          value={fmt.formatNumber(totalUnits)}
          delta={metrics.unitsSoldDelta}
          accent="primary"
          tooltip={t("restaurantAnalytics.kpiTooltips.unitsSold", { defaultValue: "Total number of menu item units sold in the period." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.itemsPerOrder", { defaultValue: "Avg. items per order" })}
          value={fmt.formatNumber(metrics.itemsPerOrder, 1)}
          delta={metrics.itemsPerOrderDelta}
          accent="success"
          tooltip={t("restaurantAnalytics.kpiTooltips.itemsPerOrder", { defaultValue: "Average number of menu items included per order." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.topSellingItem", { defaultValue: "Top selling item" })}
          value={topSellingItem ? itemName(topSellingItem.id) : "—"}
          accent="warning"
          tooltip={t("restaurantAnalytics.kpiTooltips.topSellingItem", { defaultValue: "The menu item generating the most revenue in the period." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.discountsGiven", { defaultValue: "Discounts given" })}
          value={fmt.formatCurrency(metrics.discountAmount, { compact: true })}
          delta={metrics.discountAmountDelta}
          accent="destructive"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.discountsGiven", { defaultValue: "Total value of discounts applied to orders." })}
        />
      </div>

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--two">
        <RestaurantHorizontalBars
          title={t("restaurantAnalytics.charts.topSellingItems", { defaultValue: "Top selling items" })}
          tip={t("restaurantAnalytics.chartTips.topSellingItems", { defaultValue: "The eight menu items generating the most revenue in the period." })}
          data={topItemsBars}
          valueKind="currency"
        />
        <RestaurantCategoryBars
          title={t("restaurantAnalytics.charts.revenueByCategory", { defaultValue: "Revenue by category" })}
          tip={t("restaurantAnalytics.chartTips.revenueByCategory", { defaultValue: "Revenue split across menu categories such as grills, rice dishes, and beverages." })}
          data={categoryBars}
          valueKind="currency"
          colorIndex={1}
        />
      </div>

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--menu-detail">
        <RestaurantSectionCard
          title={t("restaurantAnalytics.charts.slowMovers", { defaultValue: "Slow movers" })}
          tip={t("restaurantAnalytics.chartTips.slowMovers", { defaultValue: "The five lowest-selling menu items by units in the period." })}
          bodyClassName="restaurant-table-body"
        >
          <DashboardDataTableScroll>
            <DashboardDataTableTable className={dt.tableCompact}>
              <thead>
                <tr>
                  <th>{t("restaurantAnalytics.table.name", { defaultValue: "Name" })}</th>
                  <th className={dt.number}>{t("restaurantAnalytics.table.unitsSold", { defaultValue: "Units sold" })}</th>
                </tr>
              </thead>
              <tbody>
                {slowMovers.map(item => (
                  <tr key={item.id}>
                    <td className={dt.cellEmphasis}>{itemName(item.id)}</td>
                    <td className={dt.number}>{fmt.formatNumber(item.units)}</td>
                  </tr>
                ))}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>
        </RestaurantSectionCard>

        <RestaurantSectionCard
          title={t("restaurantAnalytics.charts.menuItemPerformance", { defaultValue: "Menu item performance" })}
          tip={t("restaurantAnalytics.chartTips.menuItemPerformance", { defaultValue: "Units, revenue, and attach rate for every menu item in the period." })}
          className="restaurant-analytics-dashboard__card--span-two"
          bodyClassName="restaurant-table-body"
          footer={(
            <TablePagePagination
              currentPage={menuPage}
              totalPages={menuTotalPages}
              onPrevious={() => setMenuPage(p => Math.max(1, p - 1))}
              onNext={() => setMenuPage(p => Math.min(menuTotalPages, p + 1))}
            />
          )}
        >
          <DashboardDataTableScroll>
            <DashboardDataTableTable className={dt.tableCompact}>
              <thead>
                <tr>
                  <th>{t("restaurantAnalytics.table.name", { defaultValue: "Name" })}</th>
                  <th>{t("restaurantAnalytics.table.category", { defaultValue: "Category" })}</th>
                  <th className={dt.number}>{t("restaurantAnalytics.table.price", { defaultValue: "Price" })}</th>
                  <th className={dt.number}>{t("restaurantAnalytics.table.unitsSold", { defaultValue: "Units sold" })}</th>
                  <th className={dt.number}>{t("restaurantAnalytics.table.revenue", { defaultValue: "Revenue" })}</th>
                  <th className={dt.number}>{t("restaurantAnalytics.table.attachRate", { defaultValue: "Attach rate" })}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMenuItems.map(item => (
                  <tr key={item.id}>
                    <td className={dt.cellEmphasis}>{itemName(item.id)}</td>
                    <td>{t(CATEGORY_LABEL_KEYS[item.category] ?? item.category, { defaultValue: item.category })}</td>
                    <td className={dt.number}>{fmt.formatCurrency(item.price)}</td>
                    <td className={dt.number}>{fmt.formatNumber(item.units)}</td>
                    <td className={dt.number}>{fmt.formatCurrency(item.revenue, { compact: true })}</td>
                    <td className={dt.number}>{fmt.formatPercent(item.attachRate, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </DashboardDataTableTable>
          </DashboardDataTableScroll>
        </RestaurantSectionCard>
      </div>
    </div>
  );
};

export default RestaurantMenuTab;
